import { useState, useEffect } from 'react';
import { api } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Plus, Building, Pencil, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const gbToBytes = (gb) => Math.max(1, Math.round(Number(gb || 0) * 1024 * 1024 * 1024));
const bytesToGb = (bytes) => ((Number(bytes || 0) / 1024 / 1024 / 1024).toFixed(2));
const mbToBytes = (mb) => Math.max(1, Math.round(Number(mb || 0) * 1024 * 1024));
const bytesToMb = (bytes) => ((Number(bytes || 0) / 1024 / 1024).toFixed(2));
const formatBytes = (bytes) => {
  const value = Number(bytes || 0);
  if (value >= 1024 * 1024 * 1024) return `${(value / 1024 / 1024 / 1024).toFixed(2)} GB`;
  if (value >= 1024 * 1024) return `${(value / 1024 / 1024).toFixed(2)} MB`;
  if (value >= 1024) return `${(value / 1024).toFixed(2)} KB`;
  return `${value} B`;
};

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingCompany, setDeletingCompany] = useState(null);
  const [formData, setFormData] = useState({ name: '', storage_limit_gb: '1', max_file_size_mb: '25' });

  const loadCompanies = () => {
    api.get('/companies').then(res => setCompanies(res.data));
  };

  useEffect(() => { loadCompanies(); }, []);

  const resetForm = () => {
    setEditingCompany(null);
    setFormData({ name: '', storage_limit_gb: '1', max_file_size_mb: '25' });
  };

  const openEdit = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      storage_limit_gb: bytesToGb(company.storage_limit_bytes),
      max_file_size_mb: bytesToMb(company.max_file_size_bytes)
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      name: formData.name,
      storage_limit_bytes: gbToBytes(formData.storage_limit_gb),
      max_file_size_bytes: mbToBytes(formData.max_file_size_mb)
    };
    try {
      if (editingCompany) {
        await api.put(`/companies/${editingCompany.id}`, payload);
        toast.success('Company updated');
      } else {
        await api.post('/companies', payload);
        toast.success('Company created');
      }
      setOpen(false);
      resetForm();
      loadCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save company');
    }
  };

  const confirmDeleteCompany = (company) => {
    setDeletingCompany(company);
    setDeleteDialogOpen(true);
  };

  const deleteCompany = async () => {
    if (!deletingCompany) return;
    try {
      await api.delete(`/companies/${deletingCompany.id}`);
      toast.success('Company deleted');
      setDeleteDialogOpen(false);
      setDeletingCompany(null);
      loadCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete company');
    }
  };

  return (
    <div className="space-y-6" data-testid="companies-page">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>Companies</h1>
        <Dialog open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="create-company-button">
              <Plus className="w-4 h-4 mr-2" />
              New Company
            </Button>
          </DialogTrigger>
          <DialogContent onInteractOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>{editingCompany ? 'Edit Company' : 'Create Company'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Company Name</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="company-name-input" />
              </div>
              <div>
                <Label>Storage Limit (GB)</Label>
                <Input type="number" min="0.01" step="0.01" value={formData.storage_limit_gb} onChange={(e) => setFormData({...formData, storage_limit_gb: e.target.value})} required data-testid="company-storage-limit-input" />
              </div>
              <div>
                <Label>Max File Size (MB)</Label>
                <Input type="number" min="0.01" step="0.01" value={formData.max_file_size_mb} onChange={(e) => setFormData({...formData, max_file_size_mb: e.target.value})} required data-testid="company-max-file-size-input" />
              </div>
              <Button type="submit" className="w-full" data-testid="submit-company-button">{editingCompany ? 'Update' : 'Create'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Company</AlertDialogTitle>
            <AlertDialogDescription>
              Delete {deletingCompany?.name} and all attached users, configs, files, batches, and send history? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteCompany} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
        {companies.map(company => {
          const used = Number(company.used_storage_bytes || 0);
          const limit = Number(company.storage_limit_bytes || 1);
          const percentage = Math.min(100, (used / limit) * 100);
          return (
            <Card key={company.id} className="glass-card" data-testid={`company-card-${company.id}`}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <CardTitle className="flex items-center gap-2">
                    <Building className="w-5 h-5" />
                    {company.name}
                  </CardTitle>
                  <Button size="sm" variant="outline" onClick={() => openEdit(company)} data-testid={`edit-company-${company.id}`}>
                    <Pencil className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => confirmDeleteCompany(company)} data-testid={`delete-company-${company.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-4 gap-3 text-sm">
                  <div><div className="text-gray-500">Users</div><div className="font-semibold">{company.user_count}</div></div>
                  <div><div className="text-gray-500">Configs</div><div className="font-semibold">{company.mail_config_count}</div></div>
                  <div><div className="text-gray-500">Files</div><div className="font-semibold">{company.file_count}</div></div>
                  <div><div className="text-gray-500">Sent</div><div className="font-semibold">{company.sent_email_count}</div></div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">Storage</span>
                    <span className="font-medium">{percentage.toFixed(1)}%</span>
                  </div>
                  <Progress value={percentage} className="h-2" />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{formatBytes(used)}</span>
                    <span>{formatBytes(limit)}</span>
                  </div>
                </div>
                <p className="text-sm text-gray-600">Created: {new Date(company.created_at).toLocaleDateString()}</p>
                <div className="rounded-lg bg-gray-50 p-2 text-xs text-gray-600">
                  <div className="font-medium text-gray-700">Company GUID</div>
                  <code className="break-all">{company.id}</code>
                </div>
                <p className="text-sm text-gray-600">Max file size: {formatBytes(company.max_file_size_bytes)}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
